import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AiInterviewData, InterviewQuestion } from '@edtech-ui/core/models/ai-interview/ai-interview-details';
import { AiInterviewServiceService } from '@edtech-ui/core/services/ai-interview-service/ai-interview-service.service';
import { ToastrService } from 'ngx-toastr';

@Component({
	selector: 'app-individual-interview-details',
	standalone: true,
	imports: [CommonModule, FormsModule, RouterModule],
	templateUrl: './individual-interview-details.component.html',
	styleUrl: './individual-interview-details.component.scss'
})
export class IndividualInterviewDetailsComponent {
	evaluationData!: AiInterviewData;
	totalScore = 0;
	feedbackLabel = '';
	role = '';
	company = '';
	round = '';
	duration = 0;

	performanceData: { title: string; subtitle: string; score: number }[] = [];
	questions: InterviewQuestion[] = [];
	strengths: string[] = [];
	focusAreas: string[] = [];
	nextSteps: { color: string; text: string }[] = [];
	recommendedSecondStep: string[] = [];

	selectedQuestion = 0;
	activeSubTab = 'feedback';
	loading = true;
	isPaid: boolean = false;
	activeTab = 'history'

	constructor(
		private service: AiInterviewServiceService,
		private route: ActivatedRoute,
		private router: Router,
		private toastr: ToastrService
	) { }

	ngOnInit(): void {
		const sessionId = this.route.snapshot.queryParamMap.get('session_id') ||
			localStorage.getItem('last_session_id');
		this.activeTab = this.route.snapshot.queryParamMap.get('tab') || '';

		if (!sessionId) {
			this.toastr.error('No session ID found.');
			this.router.navigate(['/student-dashboard/ai-interview-history']);
			return;
		}

		this.loadInterviewDetails(sessionId);
	}

	loadInterviewDetails(sessionId: string): void {
		this.loading = true;

		this.service.getInterviewDetails(sessionId).subscribe({
			next: (res) => {
				this.loading = false;
				if (res.Success && res.Data) {
					this.mapInterviewData(res.Data);
					this.isPaid = res.Data.is_paid;
				}
			},
			error: () => {
				this.loading = false;
				this.toastr.error('Failed to load interview details.');
			}
		});
	}

	mapInterviewData(data: AiInterviewData): void {
		this.evaluationData = data;
		this.totalScore = data.evaluation.total_score;
		this.feedbackLabel = data.evaluation.feedback_label;
		this.role = data.role_title;
		this.company = data.company_name;
		this.round = data.interview_round;
		this.duration = data.duration_minutes;

		const perf = data.evaluation.performance_breakdown;
		this.performanceData = [
			{ title: 'Communication', subtitle: 'Clarity and articulation', score: perf.communication },
			{ title: 'Technical Knowledge', subtitle: 'Subject matter expertise', score: perf.technical_knowledge },
			{ title: 'Confidence', subtitle: 'Self-assurance and poise', score: perf.confidence },
			{ title: 'Structure', subtitle: 'Organized responses', score: perf.structure }
		];

		this.questions = data.evaluation.questions;
		this.strengths = data.evaluation.key_strengths;
		this.focusAreas = data.evaluation.focus_areas;
		//this.recommendedSecondStep = data.evaluation.recommended_next_steps

		this.mapRecommendedSteps(data);
	}

	selectQuestion(index: number): void {
		this.selectedQuestion = index;
		this.activeSubTab = 'feedback';
	}

	getRatingText(score: number): string {
		if (score >= 8) return 'Excellent';
		if (score >= 5) return 'Fair';
		return 'Poor';
	}

	getScoreClass(score: number): string {
		if (score >= 8) return 'excellent';
		if (score >= 5) return 'average';
		return 'poor';
	}

	getProgressAngle(score: number): string {
		const percent = Math.min(Math.max(score, 0), 100);
		const angle = (percent / 100) * 360;
		return `${angle}deg`;
	}

	viewDetailedReport(): void {
		this.router.navigate(['/student-dashboard/interview-details'], {
			queryParams: { session_id: this.evaluationData.session_id }
		});
	}


	get displayScore(): number {
		return Math.floor(this.totalScore || 0);
	}

	getOneDecimalScore(score: number | null | undefined): string {
		if (score == null) return '0.0';
		return (Math.floor(score * 10) / 10).toFixed(1);
	}

	getScorePercent(score: number | null | undefined): number {
		return Math.floor((score || 0) * 10);
	}

	get isInterviewIncomplete(): boolean {
		return (
			!this.totalScore ||
			this.totalScore === 0 ||
			!this.questions?.length
		);
	}

	goback() {
		const tab = this.route.snapshot.queryParamMap.get('tab');
		if (tab === 'history') {
			this.router.navigate(['/student-dashboard/ai-interview-dashboard'], { queryParams: { tab: 'history' } });
			return;
		}
		this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
	}

	private mapRecommendedSteps(data: AiInterviewData): void {
		const steps =
			data?.evaluation?.recommended_next_steps ||
			(data as any)?.Data?.evaluation?.recommended_next_steps ||
			[];
		const colors = ['purple', 'blue', 'green', 'orange', 'red'];

		this.nextSteps = steps.length
			? steps.map((step: string, index: number) => ({
				color: colors[index % colors.length],
				text: step
			}))
			: [
				{ color: 'gray', text: 'No recommended steps available.' }
			];
	}

}